import type {
  AccountAllocationEnrichment,
  AssetAllocationPoint,
  RebalanceAccount,
  RebalanceType,
  RebalanceWithMetrics,
  Trade,
} from "../../../api/src/rebalances/rebalances.service";
import type { RiskAllocation } from "../../../api/src/securities/securities.service";
import { parseNumeric } from "../lib/numbers";
import {
  type ConsoleRebalanceMetrics,
  nullRebalanceMetrics,
} from "./nullRebalanceMetrics";
import type {
  AccountRecord,
  ExplainResponseV3,
} from "./explain/lib/explainTypes";

// Per-account manual-rebalance result blob, keyed by account id, carrying the
// risk/asset allocations the api computed in `addRiskAllocationToAccounts`.
// explain_v3 itself does not carry these, so the charts read them from here.
// `AccountAllocationEnrichment` is the SAME type the api annotates its write
// with, so the two sides can't drift; values arrive as Decimal strings or
// numbers over the wire and are coerced at the mapping edge below. Partial
// because accounts missing from the response carry no enrichment.
type ManualAccountResult = Partial<AccountAllocationEnrichment>;

export type ManualAccountResults = Record<string, ManualAccountResult>;

// Convert a weight/allocation map (Decimal-as-string or number, e.g.
// {"Growth": "0.55"}) to the numeric `RiskAllocation` the charts expect.
// Coercing to numbers matters: the chart guards treat the string "0" as truthy
// (non-empty), so leaving values as strings would render all-zero bars.
const toRiskAllocation = (
  weights: Record<string, string | number> | null | undefined,
): RiskAllocation => {
  const out: RiskAllocation = {};
  for (const [key, value] of Object.entries(weights ?? {})) {
    out[key] = parseNumeric(value);
  }
  return out;
};

// Project an api asset-allocation series to {category, allocation:number}.
const toAssetAllocation = (
  series: AssetAllocationPoint[] | null | undefined,
): { category: string; allocation: number }[] =>
  (series ?? []).map((p) => ({
    category: p.category,
    allocation: parseNumeric(p.allocation),
  }));

// Map one v3 `AccountRecord` to the `RebalanceAccount & RebalanceMetrics`
// shape that `RebalanceAccountInsights` consumes. The widget joins identity
// and the asset-allocation Current/Target series itself via its own by-id
// fetches (`useQueryAccount` / `GET /accounts/:id/allocation-details`), so
// those work for the manual flow against the real account ids. We only need
// to supply the metrics the widget reads off the prop.
const toRebalanceAccount = (
  account: AccountRecord,
  perAccount: ManualAccountResult | undefined,
): RebalanceAccount & ConsoleRebalanceMetrics => {
  const tg = account.trade_generation;

  // The widget derives buy/sell totals as `amount * price`; the v3 trade row
  // carries the dollar `value` directly, so we set amount = value, price = 1
  // (only the product is consumed). These synthesised trades are read solely
  // by RebalanceAccountInsights for its buy/sell/net totals.
  const trades: Trade[] = (tg?.trades ?? []).map(
    (t) =>
      ({
        symbol: t.symbol,
        side: t.side,
        amount: parseNumeric(t.value),
        price: 1,
      }) as Trade,
  );

  return {
    ...nullRebalanceMetrics(),
    // Not loaded: explain_v3 carries no cash settings, so the widget leaves
    // out its Cash Target rows rather than show "None" (APP-1729).
    cashSettings: undefined,
    id: account.account_id,
    // The widget shows the resolved `displayName` from its own account fetch,
    // not this field; left blank for the ephemeral object.
    accountName: "",
    trades,
    cashBalanceOverride: null,
    totalBalance: parseNumeric(account.balance),
    cashBalance: parseNumeric(account.cash_balance),
    buyTotal: parseNumeric(tg?.buy_total),
    sellTotal: parseNumeric(tg?.sell_total),
    realizedGainLossSt: parseNumeric(tg?.realized_gain_st),
    realizedGainLossLt: parseNumeric(tg?.realized_gain_lt),
    // No persisted trade timestamps in the explain payload.
    lastTradeDate: null,
    postRebalanceHoldings: [],
    // Post-Rebalance asset-allocation series. explain_v3 carries no asset
    // allocation, so this comes from the api enrichment
    // (`addRiskAllocationToAccounts` -> `assetAllocation.postRebalance`). The
    // widget renders Current/Target from its own
    // `/accounts/:id/allocation-details` fetch and only needs the
    // Post-Rebalance series from this prop.
    assetAllocation: toAssetAllocation(
      perAccount?.assetAllocation?.postRebalance,
    ),
    // Risk-allocation bars (Current / Target / Post-Rebalance). explain_v3
    // carries no per-account risk weights, so these come from the api
    // enrichment (`addRiskAllocationToAccounts`), computed server-side from
    // holdings + model + result_portfolio -- the same derivation the persisted
    // page uses in `mapMetrics`. Falls back to the explain_v3 weights (empty
    // in practice) when the enrichment is absent.
    riskAllocation: {
      current: toRiskAllocation(perAccount?.riskAllocation?.current),
      target: toRiskAllocation(
        perAccount?.riskAllocation?.target ?? account.target_risk_weights,
      ),
      postRebalance: toRiskAllocation(
        perAccount?.riskAllocation?.postRebalance ??
          account.achieved_risk_weights,
      ),
    },
    // Raise/spend-cash amount is not carried in explain_v3; only relevant for
    // raise_cash / spend_cash rebalances (the row is hidden for tolerance).
    raiseSpendCashAmount: 0,
  };
};

// Synthesise the minimal `rebalance`-shaped object the explain tree needs from
// a manual upload's `ExplainResponseV3` plus the per-account result blobs
// (`perAccountResults`, keyed by account id) the api enriched with risk/asset
// allocations. The manual rebalance is ephemeral -- there is no persisted
// `rebalance_s` row -- so the only consumer of `RebalanceContext.rebalance` in
// the explain tree (`AccountBlock`, which reads `.accounts` and `.type`) is
// served by this charts-only object. The persisted-only fields (status,
// executed times, household name) don't apply; we build just the
// charts-relevant subset and cast to the context type rather than fabricate
// those fields.
export const buildManualRebalance = (
  response: ExplainResponseV3,
  perAccountResults?: ManualAccountResults,
): RebalanceWithMetrics =>
  ({
    id: 0,
    householdId: response.request.household_id,
    type: (response.request.rebalance_type as RebalanceType) || "tolerance",
    accounts: response.accounts.map((account) =>
      toRebalanceAccount(
        account,
        perAccountResults?.[String(account.account_id)],
      ),
    ),
  }) as RebalanceWithMetrics;
